import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { redirectToCheckoutAnalyse, redirectToCheckoutPremium } from '../lib/stripe';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Target,
  Crown,
  FileText,
  Mail,
  Loader2,
  Lock,
  Sparkles,
  Zap,
  TrendingUp,
  Shield,
  ChevronRight,
  AlertCircle,
  EyeOff,
  Wrench,
  BriefcaseBusiness,
  GraduationCap,
  Globe,
  Lightbulb
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResultsPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sessionToken = searchParams.get('token') || localStorage.getItem(`analysis_token_${id}`) || null;
  const { user, isPremium, refreshUser } = useAuth();
  const navigate = useNavigate();

  const paymentSuccess = searchParams.get('payment') === 'success';

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatingCV, setGeneratingCV] = useState(false);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [cvProgress, setCvProgress] = useState(0);
  const [letterProgress, setLetterProgress] = useState(0);
  const [pollingPayment, setPollingPayment] = useState(false);

  useEffect(() => {
    if (sessionToken) localStorage.setItem(`analysis_token_${id}`, sessionToken);
    loadAnalysis();
  }, [id]);

  // After returning from Stripe, poll until webhook fires and analysis is unlocked
  useEffect(() => {
    if (!paymentSuccess) return;
    setPollingPayment(true);
    let attempts = 0;
    const MAX = 20; // poll for up to 40 seconds
    const timer = setInterval(async () => {
      attempts++;
      try {
        const params = sessionToken ? { token: sessionToken } : {};
        const { data } = await axios.get(`${API_URL}/api/analysis/${id}`, {
          params,
          withCredentials: true,
        });
        if (data.is_premium_unlocked) {
          setAnalysis(data);
          setPollingPayment(false);
          clearInterval(timer);
          return;
        }
      } catch (_) {}
      if (attempts >= MAX) {
        setPollingPayment(false);
        clearInterval(timer);
      }
    }, 2000);
    return () => { clearInterval(timer); setPollingPayment(false); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!generatingCV) { setCvProgress(0); return; }
    setCvProgress(5);
    const id = setInterval(() => setCvProgress(p => p >= 88 ? 88 : p + (88 - p) * 0.12 + 0.5), 700);
    return () => clearInterval(id);
  }, [generatingCV]);

  useEffect(() => {
    if (!generatingLetter) { setLetterProgress(0); return; }
    setLetterProgress(5);
    const id = setInterval(() => setLetterProgress(p => p >= 88 ? 88 : p + (88 - p) * 0.12 + 0.5), 700);
    return () => clearInterval(id);
  }, [generatingLetter]);

  const loadAnalysis = async () => {
    try {
      const params = sessionToken ? { token: sessionToken } : {};
      const { data } = await axios.get(`${API_URL}/api/analysis/${id}`, {
        params,
        withCredentials: true,
      });
      setAnalysis(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Analyse non trouvée');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCV = async () => {
    setGeneratingCV(true);
    try {
      const tokenQs = sessionToken ? `?token=${sessionToken}` : '';
      const { data } = await axios.post(
        `${API_URL}/api/analysis/${id}/generate-cv${tokenQs}`,
        {},
        { withCredentials: true }
      );
      setAnalysis(prev => ({ ...prev, optimized_cv: data.optimized_cv }));
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        alert('Fonctionnalité Premium requise. Débloquez votre analyse pour générer un CV optimisé.');
      } else if (status === 401) {
        alert('Votre session a expiré. Veuillez vous reconnecter.');
      } else {
        alert(err.response?.data?.detail || 'Erreur lors de la génération. Veuillez réessayer.');
      }
    } finally {
      setGeneratingCV(false);
    }
  };

  const handleGenerateLetter = async () => {
    setGeneratingLetter(true);
    try {
      const tokenQs = sessionToken ? `?token=${sessionToken}` : '';
      const { data } = await axios.post(
        `${API_URL}/api/analysis/${id}/generate-cover-letter${tokenQs}`,
        {},
        { withCredentials: true }
      );
      setAnalysis(prev => ({ ...prev, cover_letter: data.cover_letter }));
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        alert('Fonctionnalité Premium requise. Débloquez votre analyse pour générer une lettre de motivation.');
      } else if (status === 401) {
        alert('Votre session a expiré. Veuillez vous reconnecter.');
      } else {
        alert(err.response?.data?.detail || 'Erreur lors de la génération. Veuillez réessayer.');
      }
    } finally {
      setGeneratingLetter(false);
    }
  };

  const handleUnlockAnalysis = async () => {
    setUnlocking(true);
    try {
      await redirectToCheckoutAnalyse(id, sessionToken);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la redirection vers le paiement');
      setUnlocking(false);
    }
  };

  const handleUpgradePremium = async () => {
    try {
      await redirectToCheckoutPremium();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la redirection vers le paiement');
    }
  };

  const getScoreInterpretation = (score) => {
    if (score >= 80) return {
      label: "Très bon match",
      color: "text-green-600",
      bgColor: "bg-green-500",
      bgLight: "bg-green-50",
      borderColor: "border-green-200",
      message: "Ton profil correspond très bien à cette offre. Quelques optimisations peuvent encore renforcer ta candidature et maximiser tes chances.",
      urgency: "Ne laisse pas passer cette opportunité – quelques ajustements peuvent faire toute la différence."
    };
    if (score >= 60) return {
      label: "Bon match",
      color: "text-amber-600",
      bgColor: "bg-amber-500",
      bgLight: "bg-amber-50",
      borderColor: "border-amber-200",
      message: "Ton profil correspond partiellement à cette offre. Des améliorations ciblées peuvent significativement augmenter tes chances.",
      urgency: "Certaines lacunes peuvent empêcher ton CV d'être retenu par les recruteurs ou les filtres ATS."
    };
    if (score >= 40) return {
      label: "Match moyen",
      color: "text-orange-600",
      bgColor: "bg-orange-500",
      bgLight: "bg-orange-50",
      borderColor: "border-orange-200",
      message: "Ton CV ne met pas assez en valeur les compétences recherchées. Des éléments importants manquent ou ne sont pas visibles.",
      urgency: "Sans corrections, ton CV risque d'être écarté avant même d'être lu par un recruteur."
    };
    return {
      label: "Match faible",
      color: "text-red-600",
      bgColor: "bg-red-500",
      bgLight: "bg-red-50",
      borderColor: "border-red-200",
      message: "Ce poste ne semble pas correspondre à ton profil actuel. Des écarts importants ont été détectés.",
      urgency: "Ton CV sera probablement filtré automatiquement par les systèmes ATS des recruteurs."
    };
  };

  const hasAccess = analysis?.is_premium_unlocked;

  // Group missing keywords by category
  const groupKeywordsByCategory = (keywords) => {
    const groups = {
      outil: { label: "Outils & Logiciels", icon: Wrench, items: [] },
      competence: { label: "Compétences métier", icon: BriefcaseBusiness, items: [] },
      certification: { label: "Certifications & Diplômes", icon: GraduationCap, items: [] },
      langue: { label: "Langues", icon: Globe, items: [] }
    };
    
    if (!keywords || !Array.isArray(keywords)) return groups;
    
    keywords.forEach(kw => {
      if (typeof kw === 'string') {
        groups.competence.items.push({ keyword: kw, priority: "important", suggestion: "" });
      } else if (kw && typeof kw === 'object') {
        const cat = kw.category || 'competence';
        const target = groups[cat] || groups.competence;
        target.items.push(kw);
      }
    });
    
    return groups;
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critique':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700" data-testid="priority-critique">Critique</span>;
      case 'important':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700" data-testid="priority-important">Important</span>;
      case 'bonus':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-zinc-100 text-zinc-600" data-testid="priority-bonus">Bonus</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-zinc-100 text-zinc-600">—</span>;
    }
  };

  // CTA Button Component
  const UnlockCTAButton = ({ variant = "primary", className = "" }) => (
    <Button 
      onClick={handleUnlockAnalysis}
      disabled={unlocking}
      className={`${variant === "primary" 
        ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30" 
        : "bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/30"
      } font-semibold rounded-xl px-8 py-4 text-lg ${className}`}
      data-testid="unlock-cta-button"
    >
      {unlocking ? (
        <>
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          Déblocage...
        </>
      ) : (
        <>
          <Zap className="h-5 w-5 mr-2" />
          Obtenir mon CV corrigé — 9€
        </>
      )}
    </Button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
        <p className="text-zinc-600">Chargement de ton analyse...</p>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Header />
        <div className="pt-32 pb-20 text-center px-6">
          <div className="max-w-md mx-auto">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-zinc-900 mb-2">Analyse non trouvée</h2>
            <p className="text-zinc-600 mb-6">{error || 'Cette analyse n\'existe pas ou tu n\'y as pas accès.'}</p>
            <Button onClick={() => navigate('/tableau-de-bord')} className="bg-blue-600 hover:bg-blue-700">
              Retour au tableau de bord
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const scoreInfo = getScoreInterpretation(analysis.score_global);
  const keywordGroups = hasAccess ? groupKeywordsByCategory(analysis.missing_keywords) : null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Header />
      
      <div className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Back button */}
          <button
            onClick={() => navigate(user ? '/tableau-de-bord' : '/analyser')}
            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 mb-8 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4" />
            {user ? 'Retour au tableau de bord' : 'Nouvelle analyse'}
          </button>

          {/* ============ PAYMENT PROCESSING BANNER ============ */}
          {pollingPayment && (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0" />
              <div>
                <p className="text-blue-800 font-semibold text-sm">Paiement reçu — activation en cours…</p>
                <p className="text-blue-600 text-xs">Votre rapport complet sera visible dans quelques secondes.</p>
              </div>
            </div>
          )}

          {/* ============ SCORE HERO SECTION ============ */}
          <div className={`${scoreInfo.bgLight} ${scoreInfo.borderColor} border rounded-2xl p-8 mb-6`} data-testid="score-section">
            <div className="flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-shrink-0">
                <div className="relative w-36 h-36 mx-auto md:mx-0">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="72" cy="72" r="64" fill="none" stroke="#E4E4E7" strokeWidth="14" />
                    <circle 
                      cx="72" cy="72" r="64" fill="none" 
                      stroke={analysis.score_global >= 80 ? '#16A34A' : analysis.score_global >= 60 ? '#D97706' : analysis.score_global >= 40 ? '#EA580C' : '#DC2626'}
                      strokeWidth="14" 
                      strokeLinecap="round"
                      strokeDasharray={`${(analysis.score_global / 100) * 402} 402`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-5xl font-bold ${scoreInfo.color}`} data-testid="score-value">
                      {analysis.score_global}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex-1 text-center md:text-left">
                <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold ${scoreInfo.bgColor} text-white mb-4`}>
                  <Target className="h-4 w-4" />
                  {scoreInfo.label}
                </div>
                <p className="text-zinc-700 text-lg leading-relaxed" data-testid="score-interpretation">
                  {scoreInfo.message}
                </p>
              </div>
            </div>

          </div>

          {/* ============ URGENCY WARNING ============ */}
          {!hasAccess && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="urgency-warning">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium text-sm">
                  {scoreInfo.urgency}
                </p>
              </div>
            </div>
          )}

          {/* ============ STRENGTHS (LIMITED) ============ */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-6" data-testid="strengths-section">
            <h2 className="flex items-center gap-2 text-xl font-semibold text-zinc-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <CheckCircle className="h-6 w-6 text-green-600" />
              Ce qui joue en ta faveur
            </h2>
            <ul className="space-y-3">
              {(hasAccess ? analysis.strengths_preview : analysis.strengths_preview.slice(0, 1)).map((strength, index) => (
                <li key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-zinc-700">{strength}</span>
                </li>
              ))}
            </ul>
            
            {!hasAccess && analysis.strengths_preview.length > 1 && (
              <div className="mt-4 p-3 bg-zinc-100 rounded-xl border border-dashed border-zinc-300 flex items-center gap-2">
                <Lock className="h-4 w-4 text-zinc-500" />
                <span className="text-zinc-600 text-sm">+{analysis.strengths_preview.length - 1} autres atouts détectés</span>
              </div>
            )}
          </div>

          {/* ============ WEAKNESSES - LOCKED FOR FREE ============ */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-6 relative" data-testid="weaknesses-section">
            <h2 className="flex items-center gap-2 text-xl font-semibold text-zinc-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              Ce qui bloque ta candidature
            </h2>
            
            {hasAccess ? (
              <ul className="space-y-3">
                {analysis.weaknesses_preview.map((weakness, index) => (
                  <li key={index} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
                    <XCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-700">{weakness}</span>
                  </li>
                ))}
                {analysis.weaknesses_full && analysis.weaknesses_full.map((weakness, index) => (
                  <li key={`full-${index}`} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
                    <XCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-700">{weakness}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="relative">
                <div className="blur-sm select-none pointer-events-none opacity-50">
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
                        <XCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span className="text-zinc-700">{'█'.repeat(12 + i * 3)} {'█'.repeat(8 + i * 2)} {'█'.repeat(10)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 rounded-xl">
                  <div className="text-center px-6 py-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 mb-4">
                      <EyeOff className="h-7 w-7 text-amber-600" />
                    </div>
                    <div className="space-y-2 mb-4">
                      <p className="flex items-center justify-center gap-2 text-zinc-800 font-semibold">
                        <Lock className="h-4 w-4 text-red-500" />
                        6 mots-clés critiques manquants
                      </p>
                      <p className="flex items-center justify-center gap-2 text-zinc-800 font-semibold">
                        <Lock className="h-4 w-4 text-red-500" />
                        Votre CV n'est pas optimisé pour les ATS
                      </p>
                      <p className="flex items-center justify-center gap-2 text-zinc-800 font-semibold">
                        <Lock className="h-4 w-4 text-red-500" />
                        Expérience mal valorisée
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ============ FIRST CTA ============ */}
          {!hasAccess && (
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 mb-6 text-center" data-testid="cta-block-1">
              <p className="text-blue-100 text-lg mb-4">
                Votre CV est actuellement filtré. Débloquez les corrections pour passer les ATS et obtenir des entretiens.
              </p>
              <UnlockCTAButton variant="primary" className="w-full sm:w-auto" />
            </div>
          )}

          {/* ============ LOCKED PREMIUM SECTIONS ============ */}
          {!hasAccess && (
            <div className="space-y-4 mb-6">
              {/* Locked: ATS Keywords */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-md bg-white/90 z-10 flex items-center justify-center">
                  <div className="text-center px-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-3">
                      <Lock className="h-6 w-6 text-red-500" />
                    </div>
                    <p className="font-semibold text-zinc-900 mb-1">
                      {analysis.locked_missing_keywords_count || 5} mots-clés ATS manquants
                    </p>
                    <p className="text-sm text-zinc-500">Classés par priorité avec suggestions d'intégration</p>
                  </div>
                </div>
                <h2 className="flex items-center gap-2 text-lg font-medium text-zinc-400 mb-4">
                  <Target className="h-5 w-5" />
                  Mots-clés recherchés par les recruteurs
                </h2>
                <div className="flex flex-wrap gap-2">
                  {[1,2,3,4,5,6].map(i => (
                    <span key={i} className="px-3 py-1 bg-zinc-100 text-zinc-300 rounded-full text-sm">
                      {'█'.repeat(4 + i)}
                    </span>
                  ))}
                </div>
              </div>

              {/* Locked: Suggestions */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-md bg-white/90 z-10 flex items-center justify-center">
                  <div className="text-center px-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-3">
                      <Lock className="h-6 w-6 text-blue-500" />
                    </div>
                    <p className="font-semibold text-zinc-900 mb-1">Plan d'action personnalisé</p>
                    <p className="text-sm text-zinc-500">Étapes concrètes pour améliorer ton score</p>
                  </div>
                </div>
                <h2 className="flex items-center gap-2 text-lg font-medium text-zinc-400 mb-4">
                  <Sparkles className="h-5 w-5" />
                  Comment améliorer ton profil
                </h2>
                <div className="space-y-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-14 bg-zinc-100 rounded-lg" />
                  ))}
                </div>
              </div>

              {/* Locked: CV optimisé */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-md bg-white/90 z-10 flex items-center justify-center">
                  <div className="text-center px-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
                      <Lock className="h-6 w-6 text-green-500" />
                    </div>
                    <p className="font-semibold text-zinc-900 mb-1">CV optimisé prêt à envoyer</p>
                    <p className="text-sm text-zinc-500">Reformulé pour cette offre spécifique</p>
                  </div>
                </div>
                <h2 className="flex items-center gap-2 text-lg font-medium text-zinc-400 mb-4">
                  <FileText className="h-5 w-5" />
                  Ton CV reformulé
                </h2>
                <div className="space-y-2">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="h-4 bg-zinc-100 rounded" style={{ width: `${100 - i * 12}%` }} />
                  ))}
                </div>
              </div>

              {/* Locked: Lettre de motivation */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-md bg-white/90 z-10 flex items-center justify-center">
                  <div className="text-center px-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-3">
                      <Lock className="h-6 w-6 text-amber-500" />
                    </div>
                    <p className="font-semibold text-zinc-900 mb-1">Lettre de motivation personnalisée</p>
                    <p className="text-sm text-zinc-500">Générée automatiquement pour ce poste</p>
                  </div>
                </div>
                <h2 className="flex items-center gap-2 text-lg font-medium text-zinc-400 mb-4">
                  <Mail className="h-5 w-5" />
                  Ta lettre de motivation
                </h2>
                <div className="space-y-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-4 bg-zinc-100 rounded" style={{ width: `${95 - i * 8}%` }} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ============ MAIN CTA BLOCK ============ */}
          {!hasAccess && (
            <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 rounded-2xl p-8 mb-6 text-white relative overflow-hidden" data-testid="main-cta-block">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="h-6 w-6 text-amber-400" />
                  <span className="text-sm font-bold text-blue-200 uppercase tracking-wide">Déblocage instantané</span>
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Votre CV est actuellement filtré
                </h3>

                <p className="text-blue-100 text-lg mb-6 max-w-xl">
                  Débloquez les corrections pour passer les ATS et obtenir des entretiens.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-blue-100">Les erreurs exactes qui bloquent ton CV</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-blue-100">Les mots-clés à ajouter (priorisés)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-blue-100">Des exemples concrets à copier</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-blue-100">Un CV optimisé prêt à envoyer</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <Button
                    onClick={handleUnlockAnalysis}
                    disabled={unlocking}
                    className="w-full sm:w-auto bg-white hover:bg-zinc-100 text-blue-700 font-bold rounded-xl px-10 py-5 text-xl shadow-xl"
                    data-testid="main-unlock-button"
                  >
                    {unlocking ? (
                      <>
                        <Loader2 className="h-6 w-6 mr-2 animate-spin" />
                        Déblocage...
                      </>
                    ) : (
                      <>
                        <Zap className="h-6 w-6 mr-2" />
                        Obtenir mon CV corrigé — 9€
                      </>
                    )}
                  </Button>
                  <div className="text-center sm:text-left">
                    <p className="text-blue-200 text-sm">🔒 Paiement sécurisé — Résultat immédiat</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============ ACCOUNT NUDGE (anonymous) ============ */}
          {!user && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-zinc-900 text-sm">Créez un compte gratuit pour retrouver ce rapport</p>
                <p className="text-xs text-zinc-500 mt-0.5">Sans compte, ce rapport disparaît dans 48h. Créez un compte pour le conserver et analyser d'autres offres.</p>
              </div>
              <Link to="/inscription" className="flex-shrink-0">
                <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50 text-sm whitespace-nowrap">
                  Créer un compte gratuit
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          )}

          {/* ============ SECONDARY: SUBSCRIPTION ============ */}
          {!hasAccess && !isPremium && (
            <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-6" data-testid="subscription-option">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <Crown className="h-10 w-10 text-amber-500 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-zinc-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Vous postulez régulièrement ? Ne corrigez plus jamais votre CV à la main.
                  </h3>
                  <p className="text-sm text-zinc-600">
                    Analyses illimitées, CV optimisés, lettres de motivation – 29€/mois (annulable à tout moment)
                  </p>
                </div>
                <Button
                  onClick={handleUpgradePremium}
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-50 flex-shrink-0"
                  data-testid="subscription-button"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Optimiser tous mes CV – 29€/mois
                </Button>
              </div>
            </div>
          )}

          {/* ============ PREMIUM UNLOCKED CONTENT ============ */}
          {hasAccess && (
            <>
              {/* Detailed Scores */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-6" data-testid="detailed-scores">
                <h2 className="flex items-center gap-2 text-xl font-semibold text-zinc-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <Target className="h-6 w-6 text-blue-600" />
                  Scores détaillés par catégorie
                </h2>
                <p className="text-xs text-zinc-400 mb-5">Score obtenu dans chaque catégorie — combinés, ils forment le score global.</p>
                <div className="space-y-5">
                  {Object.entries(analysis.scores_detail || {}).map(([key, value]) => {
                    const labels = {
                      competences: { name: 'Compétences', weight: '30%' },
                      experience: { name: 'Expérience', weight: '25%' },
                      formation: { name: 'Formation', weight: '15%' },
                      langues: { name: 'Langues', weight: '10%' },
                      mots_cles_ats: { name: 'Mots-clés ATS', weight: '20%' }
                    };
                    const info = labels[key] || { name: key, weight: '' };
                    const scoreColor = value >= 80 ? 'text-green-600' : value >= 50 ? 'text-amber-600' : 'text-red-600';
                    const barColor = value >= 80 ? 'bg-green-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500';
                    
                    return (
                      <div key={key}>
                        <div className="flex justify-between text-sm mb-1.5">
                          <span className="text-zinc-700 font-medium">{info.name}</span>
                          <span className={`font-bold ${scoreColor}`}>{value}%</span>
                        </div>
                        <div className="h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                          <div className={`h-full ${barColor} rounded-full`} style={{ width: `${value}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ============ ATS KEYWORDS - GROUPED & PRIORITIZED ============ */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-6" data-testid="ats-keywords">
                <h2 className="flex items-center gap-2 text-xl font-semibold text-zinc-900 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                  Analyse des mots-clés ATS
                </h2>

                {/* Present keywords */}
                <div className="mb-8">
                  <h3 className="text-sm font-medium text-green-700 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Mots-clés présents dans ton CV
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {(analysis.ats_keywords || []).map((kw, i) => (
                      <span key={i} className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        {kw}
                      </span>
                    ))}
                    {(!analysis.ats_keywords || analysis.ats_keywords.length === 0) && (
                      <p className="text-sm text-zinc-500 italic">Aucun mot-clé ATS détecté dans ton CV</p>
                    )}
                  </div>
                </div>

                {/* Missing keywords - Grouped by category */}
                <div>
                  <h3 className="text-sm font-medium text-red-700 mb-4 flex items-center gap-2">
                    <XCircle className="h-4 w-4" />
                    Mots-clés manquants à ajouter
                  </h3>
                  
                  {keywordGroups && Object.entries(keywordGroups).map(([catKey, group]) => {
                    if (group.items.length === 0) return null;
                    const IconComponent = group.icon;
                    return (
                      <div key={catKey} className="mb-6 last:mb-0" data-testid={`keyword-group-${catKey}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <IconComponent className="h-4 w-4 text-zinc-500" />
                          <h4 className="text-sm font-semibold text-zinc-800">{group.label}</h4>
                        </div>
                        <div className="space-y-3">
                          {group.items.map((kw, i) => (
                            <div key={i} className="bg-red-50 border border-red-100 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-red-800">{kw.keyword}</span>
                                {getPriorityBadge(kw.priority)}
                              </div>
                              {kw.suggestion && (
                                <div className="flex items-start gap-2 mt-2">
                                  <Lightbulb className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                  <p className="text-sm text-zinc-600 italic">{kw.suggestion}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}

                  {/* Fallback if missing_keywords is flat strings */}
                  {analysis.missing_keywords && analysis.missing_keywords.length > 0 && 
                   typeof analysis.missing_keywords[0] === 'string' && (
                    <div className="flex flex-wrap gap-2">
                      {analysis.missing_keywords.map((kw, i) => (
                        <span key={i} className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Suggestions */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-6" data-testid="suggestions">
                <h2 className="flex items-center gap-2 text-xl font-semibold text-zinc-900 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <Sparkles className="h-6 w-6 text-amber-500" />
                  Comment améliorer ton profil
                </h2>
                <ul className="space-y-3">
                  {(analysis.suggestions || []).map((suggestion, index) => (
                    <li key={index} className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center text-sm">
                        {index + 1}
                      </span>
                      <span className="text-zinc-700 leading-relaxed">{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Actions: CV & Letter */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white border border-zinc-200 rounded-2xl p-6" data-testid="cv-section">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-zinc-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        CV optimisé
                      </h3>
                      <p className="text-sm text-zinc-500">Adapté à cette offre</p>
                    </div>
                  </div>
                  
                  {analysis.optimized_cv ? (
                    <Link to={`/cv-optimise/${id}`}>
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                        <FileText className="h-4 w-4 mr-2" />
                        Voir mon CV optimisé
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Button
                        onClick={handleGenerateCV}
                        disabled={generatingCV}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {generatingCV ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Génération en cours...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Générer mon CV optimisé
                          </>
                        )}
                      </Button>
                      {generatingCV && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-zinc-500 mb-1">
                            <span>{cvProgress < 30 ? "Analyse du contenu..." : cvProgress < 60 ? "Reformulation en cours..." : "Finalisation..."}</span>
                            <span>{Math.round(cvProgress)}%</span>
                          </div>
                          <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${cvProgress}%` }} />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="bg-white border border-zinc-200 rounded-2xl p-6" data-testid="letter-section">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Mail className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-zinc-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Lettre de motivation
                      </h3>
                      <p className="text-sm text-zinc-500">Personnalisée pour toi</p>
                    </div>
                  </div>
                  
                  {analysis.cover_letter ? (
                    <Link to={`/lettre/${id}`}>
                      <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white">
                        <Mail className="h-4 w-4 mr-2" />
                        Voir ma lettre
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Button
                        onClick={handleGenerateLetter}
                        disabled={generatingLetter}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                      >
                        {generatingLetter ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Génération en cours...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Générer ma lettre
                          </>
                        )}
                      </Button>
                      {generatingLetter && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-zinc-500 mb-1">
                            <span>{letterProgress < 40 ? "Analyse du poste..." : letterProgress < 70 ? "Rédaction en cours..." : "Finalisation..."}</span>
                            <span>{Math.round(letterProgress)}%</span>
                          </div>
                          <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${letterProgress}%` }} />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ============ TRUST FOOTER ============ */}
          <div className="flex items-center gap-3 p-4 bg-zinc-100 rounded-xl text-center justify-center">
            <Shield className="h-5 w-5 text-green-600" />
            <p className="text-sm text-zinc-600">
              <strong>L'IA n'invente aucune expérience ou compétence.</strong> Basé uniquement sur ton CV réel.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResultsPage;
